import React, { useMemo } from 'react'
import { type Row, useTable } from 'react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Typography, Box, Tooltip, Grid
} from '@mui/material'
import { PersonCard } from 'components'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { type DuplicateResult, type Person } from 'services'
import { DuplicateStatusIcons } from './duplicate-status-icons'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'

interface DuplicatesTableProps {
  duplicates: DuplicateResult[]
  loading: boolean
}

export const isAbleToBeDeleted = (duplicate: DuplicateResult): boolean => {
  return !duplicate.loser_has_positions &&
    !duplicate.loser_has_urls &&
    (!duplicate.loser_has_phones || duplicate.loser_has_only_dangling_phones) &&
    (!duplicate.loser_has_addresses || duplicate.loser_has_only_dangling_addresses)
}

export const DuplicatesTable: React.FC<DuplicatesTableProps> = ({ duplicates, loading }) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Original Person (Winner)',
        id: 'original',
        Cell: ({ row }: { row: Row<DuplicateResult> }) => (
          <PersonCard
            person={row.original.winner as Person}
            showUniqueId={true}
            handleEdit={(): void => {}}
            pictureLink={row.original.winner.urls[0]?.value}
            editable={false}
            filledPerson={true}
            big={true}
          />
        )
      },
      {
        Header: '',
        id: 'arrow',
        Cell: () => (
          <Box display="flex" justifyContent="center">
            <ArrowBackIcon color="primary" sx={{ fontSize: 30 }} />
          </Box>
        )
      },
      {
        Header: 'Duplicate Person (Loser)',
        id: 'duplicate',
        Cell: ({ row }: { row: Row<DuplicateResult> }) => (
          <PersonCard
            person={row.original.loser as Person}
            showUniqueId={true}
            handleEdit={(): void => {}}
            pictureLink={row.original.loser.urls[0]?.value}
            editable={false}
            filledPerson={true}
            big={true}
          />
        )
      },
      {
        Header: 'Duplicate Info',
        id: 'status',
        Cell: ({ row }: { row: Row<DuplicateResult> }) => (
          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
            <DuplicateStatusIcons
              hasPositions={row.original.loser_has_positions}
              hasPhones={row.original.loser_has_phones}
              hasUrls={row.original.loser_has_urls}
              hasOnlyDanglingPhones={row.original.loser_has_only_dangling_phones}
              hasAddresses={row.original.loser_has_addresses}
              hasOnlyDanglingAddresses={row.original.loser_has_only_dangling_addresses}
            />
          </Box>
        )
      },
      {
        Header: 'Able to be Deleted',
        id: 'deletable',
        Cell: ({ row }: { row: Row<DuplicateResult> }) => {
          return (
            <Box display="flex" justifyContent="center">
              {isAbleToBeDeleted(row.original)
                ? (
                  <Tooltip title="Ready to be deleted">
                    <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />
                  </Tooltip>
                  )
                : (
                  <Tooltip title="Cannot delete: Please remove all linked positions, URLs, and phone numbers from the duplicate person first">
                    <CancelIcon color="error" sx={{ fontSize: 24 }} />
                  </Tooltip>
                  )}
            </Box>
          )
        }
      }
    ],
    []
  )

  const tableData = useMemo(() => duplicates, [duplicates])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow
  } = useTable({
    columns,
    data: tableData
  })

  if (duplicates.length === 0) {
    return <Box display="flex" justifyContent="center">No duplicate pairs found</Box>
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Grid container alignItems="center" justifyContent="end">
        <Grid item xs display="flex" justifyContent="flex-end">
          <Typography variant="subtitle2">{duplicates.length} Duplicate Pairs</Typography>
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map(headerGroup => (
              <TableRow {...headerGroup.getHeaderGroupProps()} key={`header-${headerGroup.id}`}>
                {headerGroup.headers.map(column => (
                  <TableCell {...column.getHeaderProps()} key={`header-cell-${column.id}`} align="center">
                    <Typography fontWeight="bold">
                      {column.render('Header')}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row)
              return (
                <TableRow {...row.getRowProps()} key={`row-${row.original.loser.id}-${row.original.winner.id}`}>
                  {row.cells.map(cell => (
                    <TableCell {...cell.getCellProps()} key={`cell-${row.id}-${cell.column.id}`} align="center">
                      {cell.render('Cell')}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
